import React, { useState, useRef } from 'react';
import api from '../../services/api';
import { formatFileSize } from '../../utils/formatters';

function DocumentUpload({ leadId, onUploadComplete, onFilesChange }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const documentTypes = ['Proposal', 'Contract', 'NDA', 'Quote', 'Other'];

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const invalidFiles = selectedFiles.filter(f => {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg', 'image/png', 'image/gif', 'text/plain'
      ];
      return !validTypes.includes(f.type) && !f.name.match(/\.(pdf|doc|docx|xls|xlsx|jpg|jpeg|png|gif|txt)$/i);
    });

    if (invalidFiles.length > 0) {
      setError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}. Allowed: PDF, Word, Excel, Images, Text`);
      return;
    }

    const oversized = selectedFiles.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      setError(`Each document must be 10MB or smaller: ${oversized.map(f => f.name).join(', ')}`);
      return;
    }

    const newFiles = selectedFiles.map(f => ({
      file: f,
      name: f.name,
      size: f.size,
      type: f.type,
      documentType: 'Other',
      description: '',
      progress: 0,
      status: 'pending',
      id: Date.now() + Math.random()
    }));

    setFiles(prev => {
      const combined = [...prev, ...newFiles];
      if (combined.length > 10) {
        setError('Maximum 10 documents are allowed');
        return prev;
      }
      const totalSize = combined.reduce((sum, item) => sum + item.size, 0);
      if (totalSize > 50 * 1024 * 1024) {
        setError('Total file size exceeds 50MB limit');
        return prev;
      }
      setError('');
      if (onFilesChange) onFilesChange(combined);
      return combined;
    });
  };

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      if (onFilesChange) {
        onFilesChange(newFiles);
      }
      return newFiles;
    });
  };

  const updateFileField = (index, field, value) => {
    setFiles(prev => {
      const newFiles = prev.map((f, i) => i === index ? { ...f, [field]: value } : f);
      if (onFilesChange) {
        onFilesChange(newFiles);
      }
      return newFiles;
    });
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      setError('No files to upload');
      return;
    }

    if (!leadId) {
      setFiles(prev => prev.map(f => ({ ...f, status: 'ready' })));
      if (onUploadComplete) {
        onUploadComplete(files);
      }
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    const formData = new FormData();
    files.forEach((f, index) => {
      formData.append('documents', f.file);
      formData.append(`documentTypes[${index}]`, f.documentType || 'Other');
      formData.append(`descriptions[${index}]`, f.description || '');
    });

    try {
      const result = await api.uploadDocuments(leadId, formData, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      });

      if (result.success) {
        const uploadedDocs = result.documents || [];
        setFiles([]);
        if (onFilesChange) {
          onFilesChange([]);
        }
        if (onUploadComplete) {
          onUploadComplete(uploadedDocs);
        }
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload documents');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="document-upload">
      {error && (
        <div className="error-message" style={{ marginBottom: '12px' }}>
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <div className="document-upload-area">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={triggerFileInput}
          disabled={uploading}
        >
          <i className="fas fa-paperclip"></i>
          Attach Documents
          <span className="badge" style={{ marginLeft: '8px', fontSize: '0.6rem' }}>
            PDF, Word, Excel, Images
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <span className="text-muted" style={{ fontSize: '0.7rem', marginLeft: '12px' }}>
          Max 10 files, 50MB total
        </span>
      </div>

      {files.length > 0 && (
        <div className="document-list">
          <div className="document-list-header">
            <span>{files.length} file(s) ready to upload</span>
            {leadId ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={uploadFiles}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Uploading {uploadProgress}%
                  </>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt"></i>
                    Upload All
                  </>
                )}
              </button>
            ) : (
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                Files will be uploaded when lead is saved
              </span>
            )}
          </div>

          {files.map((file, index) => (
            <div className="document-item" key={file.id || index}>
              <div className="document-item-info">
                <i className={`fas fa-file-${getFileIcon(file.name)}`}></i>
                <span className="document-name">{file.name}</span>
                <span className="document-size">{formatFileSize(file.size)}</span>
              </div>

              <div className="document-item-fields">
                <select
                  className="form-control form-control-sm"
                  value={file.documentType}
                  onChange={(e) => updateFileField(index, 'documentType', e.target.value)}
                  disabled={uploading}
                  style={{ width: '140px' }}
                >
                  {documentTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Description (optional)"
                  value={file.description}
                  onChange={(e) => updateFileField(index, 'description', e.target.value)}
                  disabled={uploading}
                  style={{ flex: 1, minWidth: '120px' }}
                />

                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getFileIcon(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  const icons = {
    pdf: 'pdf', doc: 'word', docx: 'word',
    xls: 'excel', xlsx: 'excel',
    jpg: 'image', jpeg: 'image', png: 'image', gif: 'image',
    txt: 'alt'
  };
  return icons[ext] || 'file';
}

export default DocumentUpload;